import { CircularProgress, Collapse, Stack, Tooltip, Typography } from "@mui/material";
import NullCheck from "components/NullCheck";
import { DATE_TIME_FORMAT, MC_DEVICE_TYPE, MC_FAULT_TYPE, MC_SOFTWARE_TYPE, MC_ST_LOCATION } from "config/const";
import { useEffect, useState } from "react";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import CustomCard from "components/CustomCard";
import { findKeyByValue, getEffectiveColor, getFaultBgcolor, hasDeltaT } from "config/functions";
import CustomIconButton from "components/CustomIconButton";
import CustomButton from "components/CustomButton";
import moment from "moment";
import { setShowMassage } from "store/sysSlice";
import { useDispatch, useSelector } from "react-redux";
import { getIconByDevice, HIDE_BUTTON_POWER } from "containers/controlAndMonitoring/const";
import {
    AcUnit,
    ArrowRightAltRounded,
    BoltRounded,
    CastConnectedOutlined,
    ChangeHistoryRounded,
    FmdGoodOutlined,
    KeyboardArrowUp,
    ModeStandby,
    PowerSettingsNew,
    ReportOutlined,
    Straight,
    ThermostatRounded,
    TrackChanges,
    VerticalAlignBottom,
    VerticalAlignTop,
    WarningAmberRounded,
    WaterDropOutlined,
    Whatshot,
    WorkOutlineOutlined
} from "@mui/icons-material";
import useMc from "hooks/useMc";
import { ROLES_ID } from "config/roles";

const Details = ({ mc, onClickMcDrop, setFaultData, setMcGuidCommand, mcDropList, setMcGuidInfo }) => {
    const dispatch = useDispatch()
    const { bt } = useBizTypeTranslation();
    const userRole = useSelector((state) => state.userSettings?.user?.userRole);
    const [data, setData] = useState({})

    useEffect(() => {
        if (mc?.sensors?.length > 0) {
            const sen1 = mc?.sensors?.find(s => s?.number === '1')?.value
            const sen2 = mc?.sensors?.find(s => s?.number === '2')?.value
            const modeActive = mc?.ac?.modeActive || mc?.acThermostat?.modeActive

            const item = {
                sensor1: sen1 ? `${sen1}` : "---",
                sensor2: sen2 ? `${sen2}` : "---",
                delta: mc?.delta ? `${mc?.delta}°` : "---",
                effective: Number.isFinite(mc?.effective) ? `${mc?.effective}%` : "---",
                effectiveColor: getEffectiveColor(mc?.effective),
                setPointActive: mc?.ac?.setPointActive ? `${parseFloat(mc?.ac?.setPointActive)}` : "---",
                modeActive: modeActive ? `${bt(modeActive)}` : "---",
            }
            setData(item)
        }
    }, [mc])

    const onOpenCommandMc = (e, mc) => {
        e.stopPropagation();
        if (!mc?.isConnected)
            dispatch(setShowMassage({ massage: "ERROR_MC_NOT_CONNECTED", type: "error" }));
        else
            setMcGuidCommand(mc?.guid)
    }

    const onFaultData = (e, mcFault) => {
        e.stopPropagation();
        setFaultData(mcFault)
    }

    const onMcInfoData = (event, mc) => {
        if (userRole !== ROLES_ID.SU_ADMIN) return;
        setMcGuidInfo(mc?.guid)
        event.preventDefault()
        event.stopPropagation();
    }

    return (
        <CustomCard
            direction='column'
            spacing={2}
            padding={2}
            width={{ xs: "100%", md: 350 }}
            bgcolor={getFaultBgcolor(mc?.fault)}
            sx={{
                alignSelf: "flex-start",
                height: "auto",
                border: mc?.stLocation === MC_ST_LOCATION.INSTALLATION ? `2px solid` : `0px solid`,
                borderColor: mc?.stLocation === MC_ST_LOCATION.INSTALLATION ? 'warning.main' : 'transparent',
            }}
        >
            <Stack onClick={() => onClickMcDrop(mc)} spacing={2}>
                <Stack direction="row" justifyContent='space-between'>

                    <Stack spacing={1}>
                        <Typography color="primary.text">
                            {mc.name}
                        </Typography>

                        <Stack direction="row" spacing={2}>
                            <NullCheck valueForCheck={mc?.deviceType !== MC_DEVICE_TYPE.NONE}>
                                <Stack alignItems="center" direction="row" spacing={0.5}>
                                    {getIconByDevice(mc?.deviceType)}
                                    <Typography variant="body2" color="primary.text">
                                        {bt(findKeyByValue(MC_DEVICE_TYPE, mc?.deviceType))}
                                    </Typography>
                                </Stack>
                            </NullCheck>

                            <NullCheck valueForCheck={mc?.area?.name}>
                                <Stack alignItems="center" direction="row" spacing={0.5}>
                                    <FmdGoodOutlined fontSize="small" sx={{ color: "primary.main" }} />
                                    <Typography variant="body2" color="primary.text">
                                        {mc?.area?.name}
                                    </Typography>
                                </Stack>
                            </NullCheck>
                        </Stack>
                    </Stack>

                    <Stack direction='row' spacing={1}>

                        <NullCheck valueForCheck={mc?.fault}>
                            <Tooltip title={bt(mc?.fault)}>
                                <CustomIconButton
                                    sx={{ pt: 1.5 }}
                                    icon={<ReportOutlined sx={{ color: 'error.text' }} />}
                                    onClick={(e) => onFaultData(e, mc?.fault)}
                                    bgcolor='none'
                                    size={20}
                                />
                            </Tooltip>
                        </NullCheck>

                        <CustomIconButton
                            onClick={(event) => onMcInfoData(event, mc)}
                            // onClick={() => !mc?.isConnected ? checkConnected() : null}
                            icon={<CastConnectedOutlined
                                sx={{ color: mc?.isConnected ? 'success.main' : 'error.text' }}
                            />}
                            hideBgcolor
                            size={25}
                        />
                    </Stack>

                </Stack>

                <Stack direction="row" alignItems="center" justifyContent={HIDE_BUTTON_POWER.includes(mc?.softwareType) ? "end" : "space-between"}>
                    <NullCheck valueForCheck={!HIDE_BUTTON_POWER.includes(mc?.softwareType)}>

                        <Stack direction="row" alignItems="center" spacing={2}>
                            {mc?.isConnected
                                ? <CustomButton
                                    startIcon={<PowerSettingsNew
                                        sx={{ color: mc?.turnOnDate && mc?.isConnected ? 'success.text' : 'error.text' }}
                                    />}
                                    title={mc?.turnOnDate ? bt("ON_MC") : bt("OFF_MC")}
                                    width={100}
                                    height={44}
                                    color={mc?.turnOnDate && mc?.isConnected ? 'success.main' : 'error.main'}
                                    textColor={mc?.turnOnDate && mc?.isConnected ? 'success.text' : 'error.text'}
                                    onClick={(e) => onOpenCommandMc(e, mc)}
                                />
                                : <Typography color="error.text">
                                    {bt("MC_DISCONNECTED")}
                                </Typography>
                            }

                            <NullCheck valueForCheck={mc?.isConnected}>
                                <NullCheck valueForCheck={hasDeltaT(mc)}>
                                    <Stack alignItems="center" spacing={-0.5}>
                                        <Stack direction='row'>
                                            <BoltRounded fontSize="small" />
                                            <Typography variant="body2">
                                                {bt("EFFECTIVE")}
                                            </Typography>
                                        </Stack>

                                        <Stack alignItems={data?.sensor2 ? "start" : "center"} >
                                            {data?.effective
                                                ? <Typography color={data?.effectiveColor} variant="h6">
                                                    {data?.effective}
                                                </Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>
                                    </Stack>

                                    <Stack>
                                        <Stack direction='row' alignItems="end">
                                            <ThermostatRounded fontSize="small" />
                                            <Typography variant="body2">{bt("PLACE_TEMP")}</Typography>
                                        </Stack>

                                        <Stack direction="row" alignItems="center">
                                            {data?.sensor1
                                                ? <Typography variant="h6">{data?.sensor1}°</Typography>
                                                : <CircularProgress size={15} />
                                            }

                                        </Stack>
                                    </Stack>
                                </NullCheck>
                            </NullCheck>

                        </Stack>
                    </NullCheck>

                    <KeyboardArrowUp
                        sx={{
                            fontSize: 25,
                            transition: "transform 0.3s ease",
                            transform: mcDropList.includes(mc.id)
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                        }}
                    />

                </Stack>
            </Stack>

            <Collapse in={mcDropList.includes(mc.id)}>
                <Stack width="100%" borderTop={1} borderColor="bgDivider.main" spacing={1} py={1}>

                    <NullCheck valueForCheck={mc?.isConnected}>
                        <Stack spacing={1}>

                            {/* tmp for mc 1.1 and  mc 1.2 */}
                            <NullCheck valueForCheck={(mc?.softwareType === MC_SOFTWARE_TYPE.B1_1 ||
                                mc?.softwareType === MC_SOFTWARE_TYPE.B1_5 ||
                                mc?.softwareType === MC_SOFTWARE_TYPE.B1_8
                            )
                            }>
                                <Stack
                                    direction={mc?.softwareType === MC_SOFTWARE_TYPE.B1_5 ||
                                        mc?.softwareType === MC_SOFTWARE_TYPE.B1_8
                                        ? "column" : "row"
                                    }
                                    justifyContent="space-between"
                                    borderBottom={1}
                                    borderColor="bgDivider.main"
                                >
                                    <Stack direction="row" py={1} justifyContent="space-between">
                                        <Stack>
                                            <Stack direction='row' alignItems="center" >
                                                <ThermostatRounded fontSize="small" />
                                                <Typography variant="body2">
                                                    {bt("ACTIVE_TEMP")}
                                                </Typography>
                                            </Stack>

                                            {data?.sensor1
                                                ? <Typography variant="h6">{data?.sensor1}°</Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>

                                        <NullCheck valueForCheck={mc?.softwareType === MC_SOFTWARE_TYPE.B1_5
                                            || mc?.softwareType === MC_SOFTWARE_TYPE.B1_8
                                        }>
                                            <Stack>
                                                <Stack direction='row' alignItems="center" >
                                                    <WaterDropOutlined fontSize="small" />
                                                    <Typography variant="body2">
                                                        {bt("ACTIVE_DHT")}
                                                    </Typography>
                                                </Stack>

                                                {data?.sensor2
                                                    ? <Typography variant="h6">{data?.sensor2}%</Typography>
                                                    : <CircularProgress size={15} />
                                                }
                                            </Stack>
                                        </NullCheck>

                                    </Stack>

                                    <Stack direction="row" py={1} spacing={3} justifyContent="space-between">
                                        <Stack>
                                            <Stack direction='row' alignItems="center" spacing={0.5}>
                                                <ModeStandby fontSize="small" />
                                                <Typography variant="body2">
                                                    {bt("MODE_ACTIVE")}
                                                </Typography>
                                            </Stack>

                                            {data?.modeActive
                                                ? <Typography variant="h6">{data?.modeActive}</Typography>
                                                : <CircularProgress size={15} />
                                            }

                                        </Stack>

                                        <Stack>
                                            <Stack direction='row' alignItems="center" spacing={0.5}>
                                                <TrackChanges fontSize="small" />
                                                <Typography variant="body2">
                                                    {bt(mc?.softwareType === MC_SOFTWARE_TYPE.B1_8
                                                        ? "SET_POINT_DHT_ACTIVE"
                                                        : "SET_POINT_TEMP_ACTIVE"
                                                    )}
                                                </Typography>
                                            </Stack>

                                            {data?.setPointActive
                                                ? <Typography variant="h6">
                                                    {`${data?.setPointActive}${mc?.softwareType === MC_SOFTWARE_TYPE.B1_8 ? "%" : "°"}`}
                                                </Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </NullCheck>

                            {/* tmp for mc 1.4 */}
                            <NullCheck valueForCheck={mc?.softwareType === MC_SOFTWARE_TYPE.B1_4 ||
                                mc?.softwareType === MC_SOFTWARE_TYPE.B2_0
                            }>
                                <Stack
                                    direction="row"
                                    py={1}
                                    borderBottom={1}
                                    borderColor="bgDivider.main"
                                    justifyContent="space-between"
                                >
                                    <Stack>
                                        <Stack direction='row' alignItems="center" >
                                            <ThermostatRounded fontSize="small" />
                                            <Typography variant="body2">
                                                {bt("ACTIVE_TEMP")}
                                            </Typography>
                                        </Stack>

                                        {data?.sensor1
                                            ? <Typography variant="h6">{data?.sensor1}°</Typography>
                                            : <CircularProgress size={15} />
                                        }
                                    </Stack>

                                    <Stack>
                                        <Stack direction='row' alignItems="center" spacing={0.5}>
                                            <AcUnit fontSize="small" />
                                            <Typography variant="body2">
                                                {bt("MIN_TEMP")}
                                            </Typography>
                                        </Stack>

                                        <Typography variant="h6">
                                            {parseFloat(mc?.rangeSettings?.minTemp).toFixed(2)}°
                                        </Typography>
                                    </Stack>

                                    <Stack>
                                        <Stack direction='row' alignItems="center" spacing={0.5}>
                                            <Whatshot fontSize="small" />
                                            <Typography variant="body2">
                                                {bt("MAX_TEMP")}
                                            </Typography>
                                        </Stack>

                                        <Typography variant="h6">
                                            {parseFloat(mc?.rangeSettings?.maxTemp).toFixed(2)}°
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </NullCheck>

                            {/* tmp for mc 1.9 */}
                            <NullCheck valueForCheck={mc?.softwareType === MC_SOFTWARE_TYPE.B1_9
                            }>
                                <Stack
                                    py={1}
                                    borderBottom={1}
                                    borderColor="bgDivider.main"
                                    justifyContent="space-between"
                                    spacing={1}
                                >
                                    <Stack direction=" row" justifyContent="space-between">
                                        <Stack>
                                            <Stack direction='row' alignItems="center" >
                                                <ThermostatRounded fontSize="small" />
                                                <Typography variant="body2">
                                                    {bt("RETURN_WATER_TEMP")}
                                                </Typography>
                                            </Stack>

                                            {data?.sensor1
                                                ? <Typography variant="h6">{data?.sensor1}°</Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>


                                        <Stack>
                                            <Stack direction='row' alignItems="center" spacing={-0.5}>
                                                <Typography fontSize={16} fontWeight={600} >T</Typography>
                                                <ChangeHistoryRounded sx={{ fontSize: 16 }} />
                                                <Typography px={1} variant="body2">{bt("DELTA")}</Typography>
                                            </Stack>

                                            <Stack alignItems={data?.delta ? "start" : "center"}>
                                                {data?.delta
                                                    ? <Typography variant="h6">{data?.delta}</Typography>
                                                    : <CircularProgress size={15} />
                                                }
                                            </Stack>
                                        </Stack>


                                        <Stack>
                                            <Stack direction='row' alignItems="center" >
                                                <ThermostatRounded fontSize="small" />
                                                <Typography variant="body2">
                                                    {bt("SUPPLY_WATER_TEMP")}
                                                </Typography>
                                            </Stack>

                                            {data?.sensor2
                                                ? <Typography variant="h6">{data?.sensor2}°</Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>

                                    </Stack>



                                    <Stack
                                        alignItems="center"
                                        borderTop={1}
                                        paddingB={2}
                                        borderColor="bgDivider.main"

                                    >

                                        <Typography m={1} fontSize={14}>{bt("SETTINGS_RETURN_WATER")}</Typography>

                                        <Stack
                                            width="100%"
                                            direction="row"
                                            justifyContent="space-between"
                                        >



                                            <Stack>
                                                <Stack direction='row' alignItems="center" spacing={0.5}>
                                                    <AcUnit fontSize="small" />
                                                    <Typography variant="body2">
                                                        {bt("MIN_TEMP")}
                                                    </Typography>
                                                </Stack>

                                                <Typography variant="h6">
                                                    {parseFloat(mc?.rangeSettings?.minTemp).toFixed(2)}°
                                                </Typography>
                                            </Stack>

                                            <Stack>
                                                <Stack direction='row' alignItems="center" spacing={0.5}>
                                                    <Whatshot fontSize="small" />
                                                    <Typography variant="body2">
                                                        {bt("MAX_TEMP")}
                                                    </Typography>
                                                </Stack>

                                                <Typography variant="h6">
                                                    {parseFloat(mc?.rangeSettings?.maxTemp).toFixed(2)}°
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </NullCheck>

                            {/* temps and delta */}
                            <NullCheck valueForCheck={
                                mc?.softwareType === MC_SOFTWARE_TYPE.B1_0 ||
                                mc?.softwareType === MC_SOFTWARE_TYPE.B1_6
                            }>
                                < Stack
                                    direction='row'
                                    py={1}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    borderColor="bgDivider.main"
                                    borderBottom={1}
                                    spacing={3}
                                >
                                    <Stack>
                                        <Stack direction='row' alignItems="end">
                                            <VerticalAlignTop fontSize="small" />
                                            <Typography variant="body2">{bt("RETURN_TEMP")}</Typography>
                                        </Stack>

                                        <Stack direction="row" alignItems="center">
                                            {data?.sensor1
                                                ? <Typography variant="h6">{data?.sensor1}°</Typography>
                                                : <CircularProgress size={15} />
                                            }

                                        </Stack>
                                    </Stack>

                                    <Stack>
                                        <Stack direction='row' alignItems="end">
                                            <VerticalAlignBottom fontSize="small" />
                                            <Typography variant="body2">{bt("EXIT_TEMP")}</Typography>
                                        </Stack>

                                        <Stack direction="row" alignItems="center">
                                            {data?.sensor2
                                                ? <Typography variant="h6">{data?.sensor2}°</Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>
                                    </Stack>

                                    <Stack>
                                        <Stack direction='row' alignItems="center" spacing={-0.5}>
                                            <Typography fontSize={16} fontWeight={600} >T</Typography>
                                            <ChangeHistoryRounded sx={{ fontSize: 16 }} />
                                            <Typography px={1} variant="body2">{bt("DELTA")}</Typography>
                                        </Stack>

                                        <Stack alignItems={data?.delta ? "start" : "center"}>
                                            {data?.delta
                                                ? <Typography variant="h6">{data?.delta}</Typography>
                                                : <CircularProgress size={15} />
                                            }
                                        </Stack>
                                    </Stack>

                                    <NullCheck valueForCheck={mc?.softwareType === MC_SOFTWARE_TYPE.B1_6}>
                                        <Stack>
                                            <Stack direction='row' alignItems="center" spacing={0.5}>
                                                <ModeStandby fontSize="small" />
                                                <Typography variant="body2">
                                                    {bt("MODE_ACTIVE")}
                                                </Typography>
                                            </Stack>

                                            {data?.modeActive
                                                ? <Typography variant="h6">{data?.modeActive}</Typography>
                                                : <CircularProgress size={15} />
                                            }

                                        </Stack>
                                    </NullCheck>

                                </Stack>
                            </NullCheck>
                        </Stack>
                    </NullCheck>

                    {/* power status */}
                    <NullCheck valueForCheck={mc?.softwareType !== MC_SOFTWARE_TYPE.B2_0}>
                        <Stack direction="row" alignItems="center" justifyContent="center" >
                            {mc?.turnOnDate && (!mc?.boostPower || parseInt(mc?.boostPower) === 0)
                                ? <Typography variant="body2">
                                    {`${bt('TURN_ON_DATE')}: ${moment(mc?.turnOnDate).format(DATE_TIME_FORMAT)}`}
                                </Typography>
                                : mc?.turnOnDate && parseInt(mc?.boostPower) > 0
                                    ? <Stack alignItems="center" spacing={-0.5}>
                                        <Typography variant="body2">
                                            {`${bt('OUTSIDE_WORKING_HOURS')}`}
                                        </Typography>

                                        <Typography variant="overline">
                                            {`${bt('OFF')} - ${moment(mc?.turnOnDate).add(parseInt(mc?.boostPower), 'minutes').format(DATE_TIME_FORMAT)}`}
                                        </Typography>
                                    </Stack>
                                    : <Typography variant="body2">
                                        {`${bt('TURN_OFF_DATE')}: ${moment(mc?.turnOffDate).format(DATE_TIME_FORMAT)}`}
                                    </Typography>
                            }
                        </Stack>
                    </NullCheck>
                </Stack>
            </Collapse >

        </CustomCard >
    )
}
export default Details